import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { PeekPaper, PeekStack } from "@piiqtechnologies/ui-components";
import { useAnalytics } from "@piiqtechnologies/ui-lib";
import { useURLManager } from "@hooks";
import { AnalyticsArea, buildEventName } from "@analytics-setup/events";
import messages from "./messages.i18n";
import { CustomSubtopicsHeader, CustomSubtopicsBody, CustomSubtopicsFooter } from "./custom-subtopics-layout";
const TAB_PARAM_KEY = "subtopicTab";
const TAB_KEYS = {
    location: ["pointsOfInterest", "neighborhood"],
    amenities: ["popularAmenities", "fixedAmenities"],
    commute: ["commute"],
    "price-layout": ["studio", "oneBedroom", "twoBedrooms", "threeBedrooms"],
};
const countSubtopics = (data, keys) => keys.reduce((sum, key) => { var _a, _b; return sum + ((_b = (_a = data[key]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); }, 0);
export const CustomSubtopics = ({ maxCustomSubtopics, onSave, onDataChange, onChangeSummaryChange, fullWidth, initialData, baseSubtopicsData, readOnly, allowLayoutToggle, hasBeenRun, }) => {
    const intl = useIntl();
    const analytics = useAnalytics();
    const { getParam, setParam } = useURLManager();
    const [addedCountsPerTab, setAddedCountsPerTab] = useState({});
    const [saveTrigger, setSaveTrigger] = useState(0);
    const [unsavedChangesCount, setUnsavedChangesCount] = useState(0);
    const [pendingSave, setPendingSave] = useState(false);
    const [changeSummary, setChangeSummary] = useState({ modifiedCount: 0, customAddedCount: 0 });
    const [persistedData, setPersistedData] = useState(Object.assign({}, initialData));
    const subtopicsDataRef = useRef(Object.assign({}, initialData));
    const editingCountsPerTabRef = useRef({});
    const changeSummaryBaselineRef = useRef(null);
    useEffect(() => {
        onDataChange === null || onDataChange === void 0 ? void 0 : onDataChange(Object.assign({}, subtopicsDataRef.current));
    }, [onDataChange]);
    // If the parent provides a fresh initialData (e.g. after a refetch), reset our local baseline
    // so tab contents re-mount with the latest data.
    useEffect(() => {
        const next = Object.assign({}, initialData);
        subtopicsDataRef.current = next;
        changeSummaryBaselineRef.current = null;
        setPersistedData(next);
    }, [initialData]);
    const initialSubtopicsCount = useMemo(() => Object.fromEntries(Object.entries(TAB_KEYS).map(([tab, keys]) => [tab, countSubtopics(persistedData, keys)])), [persistedData]);
    const tabs = useMemo(() => {
        var _a, _b, _c, _d;
        return [
            {
                id: "location",
                label: intl.formatMessage(messages.tabLocation),
                count: initialSubtopicsCount["location"] + ((_a = addedCountsPerTab["location"]) !== null && _a !== void 0 ? _a : 0),
            },
            {
                id: "amenities",
                label: intl.formatMessage(messages.tabAmenities),
                count: initialSubtopicsCount["amenities"] + ((_b = addedCountsPerTab["amenities"]) !== null && _b !== void 0 ? _b : 0),
            },
            {
                id: "commute",
                label: intl.formatMessage(messages.tabCommute),
                count: initialSubtopicsCount["commute"] + ((_c = addedCountsPerTab["commute"]) !== null && _c !== void 0 ? _c : 0),
            },
            {
                id: "price-layout",
                label: intl.formatMessage(messages.tabPriceLayout),
                count: initialSubtopicsCount["price-layout"] + ((_d = addedCountsPerTab["price-layout"]) !== null && _d !== void 0 ? _d : 0),
            },
        ];
    }, [intl, initialSubtopicsCount, addedCountsPerTab]);
    const activeTabId = useMemo(() => {
        var _a, _b;
        const paramTab = getParam(TAB_PARAM_KEY);
        if (paramTab && tabs.some((tab) => tab.id === paramTab)) {
            return paramTab;
        }
        return (_b = (_a = tabs[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : "";
    }, [getParam, tabs]);
    useEffect(() => {
        if (tabs.length && !getParam(TAB_PARAM_KEY)) {
            setParam(TAB_PARAM_KEY, tabs[0].id, { replace: true });
        }
    }, [tabs, getParam, setParam]);
    const activeTabIndex = useMemo(() => {
        const index = tabs.findIndex((tab) => tab.id === activeTabId);
        return index >= 0 ? index : 0;
    }, [tabs, activeTabId]);
    const handleTabChange = useCallback((_event, newIndex) => {
        const selectedTab = tabs[newIndex];
        if (selectedTab) {
            analytics.track("Custom Subtopics | Tab Changed", { tab: selectedTab.label });
            setParam(TAB_PARAM_KEY, selectedTab.id, { replace: true });
        }
    }, [tabs, analytics, setParam]);
    const handleAddedCountChange = useCallback((tabId, count) => {
        setAddedCountsPerTab((prev) => (Object.assign(Object.assign({}, prev), { [tabId]: count })));
    }, []);
    const computeChangeSummary = useCallback((currentData) => {
        var _a;
        const baseline = (_a = changeSummaryBaselineRef.current) !== null && _a !== void 0 ? _a : {};
        let modifiedCount = 0;
        let customAddedCount = 0;
        const allKeys = new Set([...Object.keys(baseline), ...Object.keys(currentData)]);
        for (const key of allKeys) {
            const baselineRaw = baseline[key];
            const currentRaw = currentData[key];
            if (baselineRaw === null || currentRaw === null)
                continue;
            const baselineItems = baselineRaw !== null && baselineRaw !== void 0 ? baselineRaw : [];
            const currentItems = currentRaw !== null && currentRaw !== void 0 ? currentRaw : [];
            const defaultCount = baselineItems.length;
            for (let i = 0; i < currentItems.length; i++) {
                if (i < defaultCount) {
                    if (currentItems[i] !== baselineItems[i]) {
                        modifiedCount++;
                    }
                }
                else {
                    if (currentItems[i]) {
                        customAddedCount++;
                    }
                }
            }
            // Count items that were removed (baseline had more items than current) as modifications
            if (currentItems.length < baselineItems.length) {
                modifiedCount += baselineItems.length - currentItems.length;
            }
        }
        return { modifiedCount, customAddedCount };
    }, []);
    // Capture the effective baseline after all tabs have mounted and reported their initial data.
    useEffect(() => {
        if (!changeSummaryBaselineRef.current) {
            changeSummaryBaselineRef.current = Object.assign({}, subtopicsDataRef.current);
        }
    }, [persistedData]);
    const handleSubtopicsDataChange = useCallback((data) => {
        subtopicsDataRef.current = Object.assign(Object.assign({}, subtopicsDataRef.current), data);
        onDataChange === null || onDataChange === void 0 ? void 0 : onDataChange(Object.assign({}, subtopicsDataRef.current));
        // Only compute summary after baseline has been established
        if (changeSummaryBaselineRef.current) {
            const summary = computeChangeSummary(subtopicsDataRef.current);
            setChangeSummary(summary);
            onChangeSummaryChange === null || onChangeSummaryChange === void 0 ? void 0 : onChangeSummaryChange(summary);
        }
    }, [onDataChange, onChangeSummaryChange, computeChangeSummary]);
    const handleEditingCountChange = useCallback((tabId, count) => {
        editingCountsPerTabRef.current[tabId] = count;
        const total = Object.values(editingCountsPerTabRef.current).reduce((sum, c) => sum + c, 0);
        setUnsavedChangesCount(total);
    }, []);
    // When pendingSave is true and all editing rows have been flushed (unsavedChangesCount === 0),
    // the data in subtopicsDataRef.current is up to date and safe to send to the parent.
    // We also promote it to persistedData so that tab content components re-mount with the
    // saved items as their new baseline (preventing added items from disappearing on tab switch).
    useEffect(() => {
        if (pendingSave && unsavedChangesCount === 0) {
            setPendingSave(false);
            const savedData = Object.assign({}, subtopicsDataRef.current);
            // Only counts, never the subtopic text itself (may contain user-entered PII, e.g. commute addresses).
            analytics.track(buildEventName(AnalyticsArea.Subtopics, "Saved"), {
                subtopicCount: Object.values(savedData).reduce((n, arr) => { var _a; return n + ((_a = arr === null || arr === void 0 ? void 0 : arr.length) !== null && _a !== void 0 ? _a : 0); }, 0),
                categoryCount: Object.keys(savedData).length,
            });
            changeSummaryBaselineRef.current = null;
            setPersistedData(savedData);
            onSave === null || onSave === void 0 ? void 0 : onSave(savedData);
            setChangeSummary({ modifiedCount: 0, customAddedCount: 0 });
            onChangeSummaryChange === null || onChangeSummaryChange === void 0 ? void 0 : onChangeSummaryChange({ modifiedCount: 0, customAddedCount: 0 });
        }
    }, [pendingSave, unsavedChangesCount, onSave, onChangeSummaryChange, analytics]);
    const handleSave = useCallback(() => {
        setSaveTrigger((prev) => prev + 1);
        setPendingSave(true);
    }, []);
    // display-restore effect, and the update loader blocks further flips while it writes.
    const handleToggleCommit = useCallback(() => {
        if (onSave)
            handleSave();
    }, [onSave, handleSave]);
    return (React.createElement(PeekPaper, { elevation: 1, sx: Object.assign({}, (!fullWidth && { width: "80%", height: "55vh" })) },
        React.createElement(PeekStack, { height: "100%", justifyContent: "space-between" },
            React.createElement(CustomSubtopicsHeader, { tabs: tabs, activeTabId: activeTabId, activeTabIndex: activeTabIndex, customSubtopicsCount: Object.values(addedCountsPerTab).reduce((sum, c) => sum + c, 0), maxCustomSubtopics: maxCustomSubtopics, onTabChange: handleTabChange, hideCounter: readOnly }),
            React.createElement(CustomSubtopicsBody, { activeTabId: activeTabId, addedCountsPerTab: addedCountsPerTab, maxAddedSubtopics: maxCustomSubtopics, baseSubtopicsData: baseSubtopicsData, onAddedCountChange: handleAddedCountChange, onSubtopicsDataChange: handleSubtopicsDataChange, onToggleCommit: handleToggleCommit, saveTrigger: saveTrigger, onEditingCountChange: handleEditingCountChange, initialData: persistedData, readOnly: readOnly, allowLayoutToggle: allowLayoutToggle, hasBeenRun: hasBeenRun }),
            onSave && !readOnly && (React.createElement(CustomSubtopicsFooter, { onSave: handleSave, unsavedChangesCount: unsavedChangesCount, hasBeenRun: hasBeenRun, hasChanges: (!pendingSave && changeSummary.modifiedCount + changeSummary.customAddedCount > 0) ||
                    unsavedChangesCount > 0 })))));
};
export default CustomSubtopics;
